/**
 * Wire contract between the desktop connector and the server. The connector links the
 * business's WhatsApp as a companion device on the owner's computer and relays messages
 * over an outbound WebSocket (`/connector/link`), authenticated by the device token it
 * received through the device authorization flow.
 */
export namespace WhatsAppConnectorApi {
    export const LINK_PATH = '/connector/link';
    /** `DELETE` with the device token: the owner signed out in the app, which unpairs the computer. */
    export const DEVICE_PATH = '/connector/device';
    export const AUTHORIZATIONS_PATH = '/connector/authorizations';
    export const AUTHORIZATION_TOKEN_PATH = '/connector/authorizations/token';

    /**
     * Connector versions before 1.2.0 send only text, audio, image, sticker and video. A server
     * that predates a type drops its frame unacknowledged, so the connector sends it again on each
     * link until it expires from the buffer.
     */
    export type MessageContent =
        | { type: 'text'; value: string }
        | { type: 'audio'; mediaId: string }
        /**
         * `mediaId` is absent from connector versions that predate downloading that kind of media. `caption` is
         * absent from versions before 1.2.0, which relay it as a text message of its own right after its media.
         */
        | { type: 'image'; mediaId?: string; caption?: string }
        | { type: 'sticker'; mediaId?: string }
        | { type: 'video'; mediaId?: string; caption?: string }
        /**
         * `mediaId` is absent from connector versions that predate downloading documents; `fileName` and
         * `mimeType` are absent there too, and wherever the message carries none.
         */
        | { type: 'document'; mediaId?: string; fileName?: string; mimeType?: string; caption?: string }
        /** `messageId` is the id of the message reacted to. A removed reaction isn't relayed. */
        | { type: 'reaction'; messageId: string; emoji: string }
        | { type: 'location'; latitude: number; longitude: number; name: string | null; address: string | null }
        | { type: 'contacts'; contacts: Contact[] }
        /** Anything else the person sent; `kind` names it when known. */
        | { type: 'unsupported'; kind: 'poll' | null };

    export type Contact = { name: string | null; phoneNumbers: string[] };

    export type ReceivedMessage = {
        id: string;
        fromPhoneNumber: string;
        /** Unix seconds, as a string. */
        timestamp: string;
        content: MessageContent;
    };

    export type EchoedMessage = {
        id: string;
        toPhoneNumber: string;
        /** Unix seconds, as a string. */
        timestamp: string;
        content: MessageContent;
    };

    /**
     * - `pairing`: WhatsApp isn't linked yet; the connector is showing a QR code.
     * - `connecting`: linked, (re)establishing the WhatsApp session.
     * - `connected`: linked and online as `phoneNumber`.
     * - `logged_out`: WhatsApp removed this device; it must be linked again.
     */
    export type WhatsAppStatus =
        | { state: 'pairing' }
        | { state: 'connecting' }
        | { state: 'connected'; phoneNumber: string }
        | { state: 'logged_out' };

    export type Command =
        | { type: 'send'; recipientPhoneNumber: string; text: string }
        | { type: 'markAsRead'; messageId: string }
        | { type: 'readAndTyping'; messageId: string }
        | { type: 'typing'; recipientPhoneNumber: string }
        /** Media larger than `maxBytes`, or of a size the connector can't tell, fails with `media_too_large`. */
        | { type: 'downloadMedia'; mediaId: string; maxBytes?: number };

    export type CommandError = 'not_connected' | 'not_on_whatsapp' | 'message_not_found' | 'media_too_large' | 'failed';

    export type CommandResult =
        | { ok: true; messageId: string | null; base64: string | null }
        | { ok: false; error: CommandError };

    export type DeviceMessage =
        | { type: 'hello'; appVersion: string; platform: string; whatsapp: WhatsAppStatus }
        | { type: 'status'; whatsapp: WhatsAppStatus }
        | { type: 'message'; message: ReceivedMessage }
        | { type: 'echo'; message: EchoedMessage }
        | { type: 'result'; requestId: string; result: CommandResult };

    /**
     * - `welcome`: the link is up; `businessName` labels the window.
     * - `ack`: the server has the relayed message or echo `id`; until then the device keeps
     *   it and sends it again on the next link.
     * - `rejected`: the linked number can't serve this business (`phone_number_in_use`:
     *   another business already uses it). The connector unlinks WhatsApp.
     * - `revoked`: the owner disconnected this computer from the panel. The connector
     *   unlinks WhatsApp and forgets its token.
     */
    export type ServerMessage =
        | { type: 'welcome'; businessName: string }
        | { type: 'ack'; kind: 'message' | 'echo'; id: string }
        | { type: 'command'; requestId: string; command: Command }
        | { type: 'rejected'; reason: 'phone_number_in_use' }
        | { type: 'revoked' };

    export type AuthorizationStartResponse = {
        deviceCode: string;
        userCode: string;
        verificationUrl: string;
        expiresInSeconds: number;
        intervalSeconds: number;
    };

    export type AuthorizationTokenRequest = {
        deviceCode: string;
    };

    /** `pending` until the owner approves in the panel; the code then turns into a token once. */
    export type AuthorizationTokenResponse = { status: 'pending' } | { status: 'approved'; token: string };
}
